import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, retry, Subject, Subscription, take, takeUntil } from 'rxjs';

import {
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { Group } from '@shure/cloud/shared/models/devices';
import { monitorLoginState, OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	CreateGroupApiInput,
	DeleteGroupApiInput,
	GroupMutationResult,
	GroupsApiService,
	MoveGroupApiInput,
	RenameGroupApiInput
} from '../api/groups-api.service';

import {
	GroupFragment,
	GroupsQueryGQL,
	GroupsSubscriptionGQL,
	GroupSubscriptionGQL
} from './graphql/generated/cloud-sys-api';
import { mapGroupFromSysApi } from './mappers/map-group';

const PAGE_SIZE = 1000;

@Injectable({ providedIn: 'root' })
export class SysApiGroupsApiService implements GroupsApiService {
	public readonly groupsLoadingComplete$ = new BehaviorSubject<boolean>(false);
	public readonly groupAdded$ = new Subject<Group>();
	public readonly groupRemoved$ = new Subject<string>();
	public readonly groupSnapshot$ = new Subject<Group>();
	public readonly suspended$ = new Subject<void>();

	private destroy$ = new Subject<void>();
	private isInitialized = false;
	private readonly logger: ILogger;
	private readonly subscriptionReady$ = new ReplaySubject<void>(1);

	private readonly groupSubscriptionManager = new SubscriptionManager({
		subscriptionType: 'groups',
		create: (config): Subscription => this.createGroupSubscription(config),
		retryWaitMs: 10_000,
		maxRetryAttempts: 3
	});

	constructor(
		logger: ILogger,
		oktaService: OktaInterfaceService,
		private readonly groupsQueryGQL: GroupsQueryGQL,
		private readonly groupsSubscriptionGQL: GroupsSubscriptionGQL,
		private readonly groupSubscriptionGQL: GroupSubscriptionGQL
	) {
		this.logger = logger.createScopedLogger('SysApiGroupsApiService');

		monitorLoginState(oktaService, {
			onLogIn: this.initService.bind(this),
			onLogOut: this.suspendService.bind(this)
		});
	}

	public createGroup(_input: CreateGroupApiInput): Observable<GroupMutationResult> {
		throw new Error('Method not implemented.');
	}
	public deleteGroup(_input: DeleteGroupApiInput): Observable<GroupMutationResult> {
		throw new Error('Method not implemented.');
	}
	public renameGroup(_input: RenameGroupApiInput): Observable<GroupMutationResult> {
		throw new Error('Method not implemented.');
	}
	public moveGroup(_input: MoveGroupApiInput): Observable<GroupMutationResult> {
		throw new Error('Method not implemented.');
	}

	private initService(): void {
		if (this.isInitialized) {
			return;
		}

		this.isInitialized = true;
		this.destroy$ = new Subject();

		this.groupsLoadingComplete$.next(false);
		this.subscribeToGroupEvents();
		this.subscriptionReady$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
			this.queryAllGroups(null);
		});
	}

	private suspendService(): void {
		this.isInitialized = false;
		this.groupsLoadingComplete$.next(true);
		this.destroy$.next();
		this.destroy$.complete();
		this.groupSubscriptionManager.deregisterAll();

		this.suspended$.next();
	}

	private subscribeToGroupEvents(): void {
		this.groupsSubscriptionGQL
			.subscribe({ variables: {}, fetchPolicy: 'no-cache' })
			.pipe(retry({ count: 2, delay: 10_000 }), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const event = result.data?.groups;
					if (!event) {
						return;
					}

					if ('added' in event) {
						const group = mapGroupFromSysApi(event.added);
						if (group) {
							this.groupAdded$.next(group);
							this.groupSubscriptionManager.register([group.id]);
						}
					} else if ('removed' in event) {
						this.groupSubscriptionManager.deregister(event.removed);
						this.groupRemoved$.next(event.removed);
					} else if ('ready' in event) {
						this.subscriptionReady$.next();
					}
				},
				error: (error: unknown) => {
					this.logger.error('subscribeToGroupEvents', 'groups subscription error', error);
				}
			});
	}

	private queryAllGroups(afterCursor: string | null): void {
		this.groupsQueryGQL
			.fetch({
				variables: { paginationArgs: { first: PAGE_SIZE, after: afterCursor } },
				fetchPolicy: 'no-cache'
			})
			.pipe(retry({ count: 2, delay: 5_000 }), take(1), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const connection = result.data?.groupsConnection;
					if (!connection) {
						return;
					}

					for (const edge of connection.edges) {
						if (edge.node) {
							const group = mapGroupFromSysApi(edge.node);
							if (group) {
								this.groupAdded$.next(group);
								this.groupSubscriptionManager.register([group.id]);
							}
						}
					}

					if (connection.pageInfo.hasNextPage && connection.pageInfo.endCursor) {
						this.queryAllGroups(connection.pageInfo.endCursor);
					} else {
						this.groupsLoadingComplete$.next(true);
					}
				},
				error: (error: unknown) => {
					this.logger.error('queryAllGroups', 'failed to query groups', error);
					this.groupsLoadingComplete$.next(true);
				}
			});
	}

	private createGroupSubscription({
		id,
		retryCallback,
		eventObserver
	}: SubscriptionManagerConfigCreate): Subscription {
		return this.groupSubscriptionGQL
			.subscribe({
				variables: {
					id
				},
				errorPolicy: 'ignore',
				fetchPolicy: 'network-only'
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const groupData = result.data?.nodeChanges;

					if (groupData && '__typename' in groupData && groupData.__typename === 'Group') {
						const group = mapGroupFromSysApi(groupData as GroupFragment);
						if (group) {
							this.groupSnapshot$.next(group);
						}
					}

					if (eventObserver) {
						eventObserver.eventReceived();
					}
				},
				complete: () => {
					this.logger.error(
						'groupSubscriptionGQL',
						'subscription completed. Will retry.',
						JSON.stringify({ nodeId: id })
					);
					retryCallback();
				},
				error: (error: unknown) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'groupSubscriptionGQL',
						'Encountered retryable error',
						JSON.stringify({ id, error }, null, 3)
					);
					retryCallback();
				}
			});
	}
}
